<template>
  <div class="kanban-container">
    <div 
      v-for="status in statuses" 
      :key="status" 
      class="kanban-column"
    >
      <div class="column-header">
        <h3 class="column-title">{{ formatStatus(status) }}</h3>
        <span class="column-count">{{ getLessonsByStatus(status).length }}</span>
      </div>
      
      <div class="column-content">
        <div 
          v-for="lesson in getLessonsByStatus(status)" 
          :key="lesson.id" 
          class="kanban-card"
          draggable="true"
          @dragstart="onDragStart($event, lesson)"
          @dragover.prevent
          @drop="onDrop($event, status)"
        >
          <div class="card-header">
            <div class="card-thumbnail">
              <img 
                :src="lesson.lesson_temp_file || 'https://via.placeholder.com/300x200?text=No+Thumbnail'" 
                :alt="lesson.title"
                class="thumbnail-img"
              >
            </div>
            <div class="card-actions">
              <button @click="$emit('play-lesson', lesson)" class="card-action-button play" title="Play Lesson">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="action-icon">
                  <polygon points="5 3 19 12 5 21 5 3"/>
                </svg>
              </button>
              <button @click="$emit('edit-lesson', lesson)" class="card-action-button edit" title="Edit Lesson">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="action-icon">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              </button>
            </div>
          </div>
          
          <div class="card-content">
            <h4 class="card-title">{{ lesson.title }}</h4>
            <p class="card-description">{{ lesson.description }}</p>
            
            <div class="card-meta">
              <div class="meta-item">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="meta-icon">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
                <span>{{ formatDuration(lesson.duration) }}</span>
              </div>
              <div class="meta-item">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="meta-icon">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                </svg>
                <span>{{ lesson.course?.title }}</span>
              </div>
            </div>
          </div>
          
          <div class="card-footer">
            <div class="progress-bar">
              <div 
                class="progress-fill" 
                :style="{ width: `${getProgressPercentage(lesson) * 100}%` }"
                :class="getProgressClass(lesson)"
              ></div>
            </div>
            <span class="progress-text">{{ Math.round(getProgressPercentage(lesson) * 100) }}%</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LessonsKanban',
  props: {
    filteredLessons: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      statuses: ['not_started', 'in_progress', 'completed', 'pending', 'uploaded'],
      draggedLesson: null
    }
  },
  methods: {
    getLessonsByStatus(status) {
      return this.filteredLessons.filter(lesson => lesson.lesson_status === status);
    },
    formatStatus(status) {
      return status.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    },
    getProgressPercentage(lesson) {
      if (lesson.completion_percentage) {
        return lesson.completion_percentage / 100;
      }
      
      if (lesson.lesson_status === 'completed') {
        return 1;
      } else if (lesson.lesson_status === 'in_progress') {
        return Math.max(0.1, Math.min(0.9, (lesson.id % 9) / 10 + 0.1));
      }
      return 0;
    },
    getProgressClass(lesson) {
      const percentage = this.getProgressPercentage(lesson);
      if (percentage >= 1) {
        return 'progress-completed';
      } else if (percentage > 0) {
        return 'progress-in-progress';
      }
      return 'progress-not-started';
    },
    onDragStart(event, lesson) {
      this.draggedLesson = lesson;
      event.dataTransfer.effectAllowed = 'move';
    },
    onDrop(event, newStatus) {
      event.preventDefault();
      if (this.draggedLesson) {
        this.$emit('update-status', this.draggedLesson.id, newStatus);
        this.draggedLesson = null;
      }
    },
    formatDuration(seconds) {
      if (!seconds && seconds !== 0) return '0 min';
      return `${Math.round(seconds / 60)} min`;
    }
  }
}
</script>

<style scoped>
.kanban-container {
  display: flex;
  gap: 1.5rem;
  overflow-x: auto;
  padding: 1rem;
  min-height: calc(100vh - 200px);
}

.kanban-column {
  min-width: 300px;
  max-width: 300px;
  background: #f8fafc;
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.column-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem;
  background: white;
  border-radius: 6px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.column-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  text-transform: capitalize;
}

.column-count {
  font-size: 0.75rem;
  color: #64748b;
  background: #f1f5f9;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
}

.column-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
}

.kanban-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: all 0.2s ease;
  cursor: grab;
}

.kanban-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.kanban-card:active {
  cursor: grabbing;
}

.card-header {
  position: relative;
}

.card-thumbnail {
  width: 100%;
  height: 120px;
  overflow: hidden;
}

.thumbnail-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.card-actions {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: flex;
  gap: 0.5rem;
}

.card-action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
}

.card-action-button.play {
  color: #1d4ed8;
}

.card-action-button.play:hover {
  background: #dbeafe;
  transform: translateY(-1px);
}

.card-action-button.edit {
  color: #166534;
}

.card-action-button.edit:hover {
  background: #dcfce7;
  transform: translateY(-1px);
}

.action-icon {
  width: 14px;
  height: 14px;
}

.card-content {
  padding: 1rem;
}

.card-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.5rem;
}

.card-description {
  font-size: 0.75rem;
  color: #64748b;
  margin: 0 0 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-meta {
  display: flex;
  gap: 1rem;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: #64748b;
}

.meta-icon {
  width: 14px;
  height: 14px;
}

.card-footer {
  padding: 0.75rem 1rem;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
}

.progress-bar {
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.progress-fill {
  height: 100%;
  transition: width 0.3s ease;
}

.progress-completed {
  background: #10b981;
}

.progress-in-progress {
  background: #3b82f6;
}

.progress-not-started {
  background: #94a3b8;
}

.progress-text {
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 500;
}

@media (max-width: 768px) {
  .kanban-container {
    flex-direction: column;
    overflow-x: hidden;
    overflow-y: auto;
  }
  
  .kanban-column {
    min-width: 100%;
    max-width: 100%;
  }
}
</style> 